import { g } from "analytics";
import { removeDiacritics } from "diacritics";
import { t } from "i18next";
import { clog } from "log";
import MiniSearch from "minisearch";
const IGNORE_TERMS = {
    "fr": ["a", "à"],
    "es": [],
    "de": [],
    "en": ["a", "an"],
};
const TOKENIZE_SETTINGS = [
    {
        type: "add",
        match: { type: "include", substring: "meter" },
        exclude: [
            { type: "exact", string: "diameter" },
        ],
        terms: ["meter", "sensor"]
    }
];
function matchSubstring(string, match) {
    if (match.type === "include") {
        return string.indexOf(match.substring) > -1;
    }
    else if (match.type === "exact") {
        return string === match.string;
    }
    return false;
}
export function tokenize(string) {
    const defaultTokenize = MiniSearch.getDefault('tokenize');
    const results = [...defaultTokenize(string)];
    for (const setting of TOKENIZE_SETTINGS) {
        if (setting.type === "add") {
            if (matchSubstring(string, setting.match)) {
                let excluded = false;
                for (const exclude of setting.exclude) {
                    if (matchSubstring(string, exclude)) {
                        excluded = true;
                        break;
                    }
                }
                if (!excluded) {
                    results.push(...setting.terms);
                }
            }
        }
    }
    return results;
}
export function createMiniSearch(compiledApparatusSpecs, currentLanguage) {
    clog("Creating search for", currentLanguage);
    const minisearch = new MiniSearch({
        idField: "type",
        fields: ["name", "search", "prioritySearch"],
        storeFields: ["type", "category", "sprite", "released", "name", "tier"],
        searchOptions: {
            prefix: true,
            fuzzy: 0.28,
            boost: { name: 1.5, prioritySearch: 1.5, search: 0.7 },
            tokenize: MiniSearch.getDefault('tokenize'),
            weights: { fuzzy: 0.2, prefix: 0.8 },
        },
        tokenize,
        processTerm(term, fieldName) {
            var _a;
            // Ignore certain terms.
            const ignore_terms = (_a = IGNORE_TERMS[currentLanguage]) !== null && _a !== void 0 ? _a : IGNORE_TERMS["en"];
            if (ignore_terms.indexOf(term) > -1) {
                return [];
            }
            // Case insensitive
            term = term.toLowerCase();
            const results = [term];
            // Include common misspelling of burette
            if (term.indexOf("burette") > -1) {
                results.push(term.replace("burette", "burrette"));
            }
            // Diacritics
            var simplified = removeDiacritics(term);
            if (simplified !== term) {
                results.push(simplified);
            }
            return results;
        },
    });
    minisearch.addAll(compiledApparatusSpecs);
    return minisearch;
}
export function searchApparatusUsingMiniSearch(minisearch, term) {
    const results = minisearch.search(term);
    // console.log(results)
    return results;
}
export function searchApparatus(term, inList) {
    if (term.length == 0) {
        return [];
    }
    // Search is case-insensitive.
    term = term.trim().toLowerCase();
    var results = [];
    for (let a of inList) {
        if (a.type.indexOf(term) !== -1) {
            // Lower score means better match.
            a.searchScore = 0;
            results.push(a);
        }
        else if (a.lowerCaseName.indexOf(term) !== -1) {
            // Search in the name as well.
            // Lower score means better match.
            a.searchScore = 0;
            results.push(a);
        }
        else {
            // search in additional terms
            a.search.some((searchTerms, i) => {
                if (searchTerms.indexOf(term) !== -1) {
                    a.searchScore = 1 + i;
                    results.push(a);
                    return true;
                }
                return false;
            });
        }
    }
    // Sort the results
    results.sort(function (a, b) {
        // Compare by search score first
        var scoreDiff = (a.searchScore || 0) - (b.searchScore || 0);
        if (scoreDiff !== 0) {
            return scoreDiff;
        }
        // Compare based on name
        if (a.type < b.type) {
            return -1;
        }
        return 1; // Equal names are (technically) not possible
    });
    return results;
}
export const NO_SEARCH_TERM_SENTINEL = '__nosearchtermsentinel__';
/**
 * Converts a comma separated string of search terms, into a string[]
 * First, we split on ";". Anythin before are considered priority search terms. Everything else is regular
 * If no ";" present, all considered regular.
 */
export function compileSearchTerms(searchTermsString, name) {
    // Search terms can be specified as:
    //   - term1,term2,...
    //   or
    //   - priorityTerm1,priorityTerm2...;term1,term2,...
    // Trailing and leading spaces for each term are trimmed. Each term is lowercased.
    //
    // Re-include name as a search term to boost term relevance across at least one more field.
    // Consider:
    // - name: Beaker
    // - name: Beaker Tongs, search: Beaker
    // In this case, searching for "Beaker" should return both, but Beaker should be ranked higher.
    // This is achieved by adding "Beaker" to the search terms of Beaker, but as a priority search term, to beat
    // the match of "beaker" on the tongs.
    const prioritySearch = [];
    const search = [name.toLocaleLowerCase()];
    if (searchTermsString === NO_SEARCH_TERM_SENTINEL) {
        return { prioritySearch, search };
    }
    const partitionedSearchTermsString = searchTermsString.split(";");
    if (partitionedSearchTermsString.length == 1) {
        search.push(...compileSearchTermsInner(partitionedSearchTermsString[0]));
    }
    else if (partitionedSearchTermsString.length == 2) {
        prioritySearch.push(...compileSearchTermsInner(partitionedSearchTermsString[0]));
        search.push(...compileSearchTermsInner(partitionedSearchTermsString[1]));
    }
    else {
        throw new Error("Unexpected search term string: " + searchTermsString);
    }
    return { prioritySearch, search };
}
/**
 * Processes the terms, no ";" expected.
 */
function compileSearchTermsInner(searchTermsString) {
    if (searchTermsString === "") {
        return [];
    }
    var terms = [];
    for (const rawTerm of searchTermsString.split(",")) {
        const term = rawTerm.trim().toLowerCase();
        terms.push(term);
    }
    return terms;
}
/**
 * Checks if two terms are similar enough
 * Returns true if "next" is a better term to report
 */
function shouldReplace(previous, next) {
    if (previous.indexOf(next) > -1) {
        // Next is a substring of previous, so is not better.
        return false;
    }
    return true;
}
export class NoSearchResultsReporter {
    report(term) {
        // Reject terms that are too short.
        if (!term) {
            return;
        }
        if (term.length < 3) {
            return;
        }
        // Check reported term first before checking last sent term.
        if (this.lastReportedTerm) {
            if (shouldReplace(this.lastReportedTerm, term)) {
                this.sendWithDelay(term);
            }
        }
        else if (this.lastSentTerm) {
            if (shouldReplace(this.lastSentTerm, term)) {
                this.sendWithDelay(term);
            }
        }
        else {
            // Neither is set.
            this.sendWithDelay(term);
        }
    }
    sendWithDelay(term) {
        this.lastReportedTerm = term;
        if (this.timeout !== undefined) {
            clearTimeout(this.timeout);
        }
        this.timeout = window.setTimeout(() => {
            // TODO log also on language changes
            const currentLanguage = t("currentLanguageShort");
            g("No search results", `${currentLanguage}:${this.lastReportedTerm}`);
            this.lastSentTerm = this.lastReportedTerm;
            this.lastReportedTerm = undefined;
            this.timeout = undefined;
        }, NoSearchResultsReporter.DELAY_SEND_MS);
    }
}
NoSearchResultsReporter.DELAY_SEND_MS = 2000;
